import {
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from "@chakra-ui/react";
import EmojiPicker, { EmojiClickData, EmojiStyle } from "emoji-picker-react";
import { FunctionComponent, KeyboardEvent } from "react";

export interface EmojiInputProps {
  name?: string;
  value?: string;
  onChange?: (emoji: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const EmojiInput: FunctionComponent<EmojiInputProps> = ({
  name,
  value = "",
  onChange,
  placeholder,
  disabled,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    onChange?.(emojiData.emoji);
    onClose();
  };

  // Special handling to allow deleting the emoji since the field is read-only.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled || !value) return;
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      onChange?.("");
    }
  };

  return (
    <Popover
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      arrowSize={10}
      // The emoji-picker-react picker is slow to render, so we wait to render
      // it until the popover is opened, and then avoid re-rendering it when the
      // popover is closed.
      isLazy
      lazyBehavior="keepMounted"
    >
      <PopoverTrigger>
        <Input
          width="2.5em"
          padding="0"
          textAlign="center"
          value={value}
          placeholder={placeholder}
          name={name}
          readOnly
          disabled={disabled}
          onClick={() => !disabled && onOpen()}
          onKeyDown={handleKeyDown}
          cursor={disabled ? "not-allowed" : "pointer"}
        />
      </PopoverTrigger>
      <PopoverContent
        width="auto"
        padding={0}
        onBlur={(e) => {
          // This prevents blur events from the picker from bubbling up, so that
          // only blur events originating from the input field itself propagate
          // outside this component.
          e.stopPropagation();
        }}
      >
        <PopoverArrow />
        <PopoverBody
          onBlur={(e) => {
            // The emoji-picker-react component generates a blur event with a
            // `null` relatedTarget when moving the mouse between the emoji list
            // and the bottom preview that would cause the popover to close
            // spontaneously. This check prevents that spurious closure.
            if (e.relatedTarget === null) e.stopPropagation();
          }}
        >
          <EmojiPicker
            emojiStyle={EmojiStyle.NATIVE}
            onEmojiClick={handleEmojiSelect}
            allowExpandReactions={false}
            style={PickerStyle}
            lazyLoadEmojis={true}
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

// The emoji-picker-react component seems to do a full re-render when the
// `style` prop changes, so we make it a constant.
const PickerStyle = { border: "none" } as const;
